import React, { useState, useRef, useEffect } from 'react';
import {
  countriesByDefault,
  getCountryByDialCode,
  parseE164,
  CountryOption,
} from '../../data/countries';
import './PhoneInput.scss';

export interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  id?: string;
}

const defaultCountry = countriesByDefault[0]; // Morocco

/**
 * Build full E.164 digits from country and national number.
 * Strips leading 0 from national (e.g. 06... in Morocco).
 */
function toE164(country: CountryOption, national: string): string {
  const digits = national.replace(/\D/g, '');
  const withoutLeadingZero = digits.replace(/^0+/, '') || digits;
  return country.dialCode + withoutLeadingZero;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onBlur,
  label,
  error,
  required,
  placeholder,
  disabled,
  fullWidth = false,
  id: propId,
}) => {
  const id = propId || 'phone-input';
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const parsed = value ? parseE164(value) : null;
  const [selectedCountry, setSelectedCountry] = useState<CountryOption>(
    () => parsed?.country ?? defaultCountry
  );
  const [nationalNumber, setNationalNumber] = useState(() => parsed?.national ?? '');

  // Sync from external value (e.g. prefill)
  useEffect(() => {
    if (value) {
      const p = parseE164(value);
      if (p) {
        setSelectedCountry(p.country);
        setNationalNumber(p.national);
      }
    } else {
      setNationalNumber('');
    }
  }, [value]);

  const filteredCountries = search.trim()
    ? countriesByDefault.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.nameEn.toLowerCase().includes(search.toLowerCase()) ||
          c.dialCode.includes(search)
      )
    : countriesByDefault;

  const handleCountrySelect = (country: CountryOption) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearch('');
    const full = toE164(country, nationalNumber);
    if (full.length >= 10) onChange(full);
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '');
    setNationalNumber(v);
    const full = toE164(selectedCountry, v);
    onChange(full);
  };

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`phone-input ${fullWidth ? 'phone-input--full-width' : ''}`}
    >
      {label && (
        <label htmlFor={id} className="phone-input__label">
          {label}
          {required && <span className="phone-input__required">*</span>}
        </label>
      )}
      <div
        className={`phone-input__wrapper ${error ? 'phone-input__wrapper--error' : ''} ${disabled ? 'phone-input__wrapper--disabled' : ''}`}
      >
        <button
          type="button"
          className="phone-input__trigger"
          onClick={handleOpen}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={`Pays: ${selectedCountry.name}, indicatif +${selectedCountry.dialCode}`}
        >
          <img
            src={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png`}
            alt=""
            className="phone-input__flag-img"
          />
          <span className="phone-input__dial">+{selectedCountry.dialCode}</span>
          <span className="phone-input__chevron" aria-hidden>▼</span>
        </button>

        {isOpen && (
          <div className="phone-input__dropdown" role="listbox">
            <input
              ref={searchRef}
              type="text"
              className="phone-input__search"
              placeholder="Rechercher un pays..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <ul className="phone-input__list">
              {filteredCountries.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    className={`phone-input__option ${c.code === selectedCountry.code ? 'phone-input__option--selected' : ''}`}
                    onClick={() => handleCountrySelect(c)}
                    role="option"
                    aria-selected={c.code === selectedCountry.code}
                  >
                    <img
                      src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`}
                      alt=""
                      className="phone-input__option-flag"
                    />
                    <span className="phone-input__option-name">{c.name}</span>
                    <span className="phone-input__option-dial">+{c.dialCode}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <input
          id={id}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          className="phone-input__number"
          placeholder={placeholder ?? '6 12 34 56 78'}
          value={nationalNumber}
          onChange={handleNationalChange}
          onBlur={onBlur}
          disabled={disabled}
        />
      </div>
      {error && <span className="phone-input__error">{error}</span>}
    </div>
  );
};

export default PhoneInput;
